import React, { useEffect, useState } from 'react';
import { authFetch } from '../compat/api';
import './ApplicationNoteModal.css';

/**
 * The note editor, shared by Application Portfolio Q&A and Initial Portfolio Scoring.
 *
 * One component rather than one per screen. The two screens have drifted before — the
 * same status was a chip on one and plain text on the other — and a note that could be
 * written on one screen and only half-read on the other would be the same mistake with
 * worse consequences, because the text is the user's own words.
 *
 * The server decides who may write; this only reflects it. `readOnly` dims the box for a
 * caller who cannot save, but the guard that matters is `allowApplication` in notes.ts —
 * an Application Owner is refused another owner's application there, whatever the client
 * sends.
 */

export interface ApplicationNoteModalProps {
  applicationId: string;
  applicationName: string;
  applicationCode?: string;
  readOnly?: boolean;
  onClose: () => void;
  /** Called after a successful save so the opening screen can refresh its indicator. */
  onSaved?: (applicationId: string, note: string) => void;
}

export default function ApplicationNoteModal({
  applicationId,
  applicationName,
  applicationCode,
  readOnly = false,
  onClose,
  onSaved,
}: ApplicationNoteModalProps) {
  const [note, setNote] = useState('');
  const [original, setOriginal] = useState('');
  const [meta, setMeta] = useState<{ updatedBy: string | null; updatedAt: string | null }>({
    updatedBy: null,
    updatedAt: null,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await authFetch(`/api/notes/${applicationId}`);
        if (!response.ok) throw new Error(`Could not load the note (HTTP ${response.status})`);
        const data = await response.json();
        if (cancelled) return;
        setNote(data.note ?? '');
        setOriginal(data.note ?? '');
        setMeta({ updatedBy: data.updatedBy ?? null, updatedAt: data.updatedAt ?? null });
      } catch (err: any) {
        if (!cancelled) setError(err.message || 'Could not load the note.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [applicationId]);

  const dirty = note.trim() !== original.trim();

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const response = await authFetch(`/api/notes/${applicationId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note }),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || body.error || `Save failed (HTTP ${response.status})`);
      }
      const data = await response.json();
      setOriginal(data.note ?? '');
      setNote(data.note ?? '');
      setMeta({ updatedBy: data.updatedBy ?? null, updatedAt: data.updatedAt ?? null });
      onSaved?.(applicationId, data.note ?? '');
      onClose();
    } catch (err: any) {
      // Kept open on failure. Closing would discard what the user typed to tell them
      // it had not been saved, which is the one moment losing it is unforgivable.
      setError(err.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="note-overlay" role="dialog" aria-modal="true" aria-label="Application note">
      <div className="note-modal">
        <div className="note-modal__head">
          <div>
            <div className="note-modal__title">Application note</div>
            <div className="note-modal__sub">
              {applicationCode ? <span className="note-modal__code">{applicationCode}</span> : null}
              {applicationName}
            </div>
          </div>
          <button className="note-modal__x" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="note-modal__body">
          <p className="note-modal__hint">
            Context the score cannot carry — a contract date, a dependency, a reason the
            answers read the way they do. Visible on Initial Portfolio Scoring.
          </p>

          {loading ? (
            <div className="note-modal__loading">Loading…</div>
          ) : (
            <textarea
              className="note-modal__text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              readOnly={readOnly}
              placeholder={readOnly ? 'No note recorded.' : 'Add a note for this application…'}
              rows={9}
              autoFocus={!readOnly}
            />
          )}

          {meta.updatedBy ? (
            <div className="note-modal__meta">
              Last saved by {meta.updatedBy}
              {meta.updatedAt ? ` on ${new Date(meta.updatedAt).toLocaleString()}` : ''}
            </div>
          ) : null}

          {error ? <div className="note-modal__error">{error}</div> : null}
        </div>

        <div className="note-modal__foot">
          <button className="note-btn" onClick={onClose}>
            {readOnly ? 'Close' : 'Cancel'}
          </button>
          {!readOnly && (
            <button
              className="note-btn note-btn--primary"
              onClick={save}
              disabled={saving || loading || !dirty}
              title={!dirty ? 'Nothing has changed' : undefined}
            >
              {saving ? 'Saving…' : 'Save note'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * The control that opens the editor from a table row.
 *
 * Carries whether a note exists so the row can say so at a glance — a portfolio where
 * three applications out of six hundred have context is useless if you have to open all
 * six hundred to find them.
 */
export function ApplicationNoteButton({
  hasNote,
  onClick,
  label,
}: {
  hasNote: boolean;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  label?: string;
}) {
  return (
    <button
      className={`note-open-btn${hasNote ? ' note-open-btn--has' : ''}`}
      onClick={onClick}
      title={hasNote ? 'View or edit the note on this application' : 'Add a note to this application'}
    >
      <svg className="note-open-btn__icon" viewBox="0 0 12 12" aria-hidden="true">
        <path
          d="M2 1.6h8v6.2l-2.4 2.6H2z"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.2"
          strokeLinejoin="round"
        />
        <path d="M3.6 4.2h4.8M3.6 6.2h3.2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
      </svg>
      {label ?? (hasNote ? 'Note' : 'Add note')}
    </button>
  );
}
